import { useState, useEffect, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet-async";

import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiCard from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import Button from "@mui/material/Button"
import InputAdornment from "@mui/material/InputAdornment";
import MuiFormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

import FrontHeader from "../../components/front-end/Header";
import Footer from "../../components/front-end/footer";
import CsInput from "../../components/input";
import Error from "../../components/error-msg";
import SearchSelect from "../../components/search-select";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CsTooltip from "../../components/tooltip";
import { checkSpace, validateEmail, validateName } from "../../helper_functions/Functions";
import { fetchAllList } from "../../redux/common/commonActions";
import { showLoader, showToast } from "../../redux/common/commonActions";
import { getUserSuccess, setReferralLink, setLinkUTM } from "../../redux/user/userActions";
import { userRegistrationShortened } from "../../api_endpoints/UserLoginSignupAPI";

import GoogleSignup from "./GoogleSignup";

// ** Styled Components
const Card = styled(MuiCard)(({ theme }) => ({
  [theme.breakpoints.up("sm")]: { width: "33rem" },
}));

const LinkStyled = styled("a")(({ theme }) => ({
  fontSize: "0.875rem",
  textDecoration: "none",
  color: theme.palette.primary.main,
  cursor: "pointer",
}));

const FormControlLabel = styled(MuiFormControlLabel)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  marginBottom: theme.spacing(5),
  "& .MuiFormControlLabel-label": {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
}));

const RegisterPage = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const refLink = useSelector((state) => state.userState.referralLink);
  const { referralLink } = useParams();
  const utmLink = useSelector((state) => state.userState.utmLink);
  const commonStateCountryData = useSelector((state) => state.commonState?.countryData?.data);

  const [countryData, setCountryData] = useState(commonStateCountryData ?? [])
  const [fname, setFname] = useState("");
  const [fnameError, setFnameError] = useState(false);
  const [nameErrorMsg, setNameErrorMsg] = useState("please fill the name");
  const [lname, setLname] = useState("");
  const [lnameError, setLnameError] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passErrMsg, setPassErrMsg] = useState("")
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState(false);
  const [country, setCountry] = useState(null);
  const [countryError, setCountryError] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [isAgreedError, setIsAgreedError] = useState(0);

  const [otpSection, setOtpSection] = useState({
    emailOTP: "",
    phoneOTP: "",
    emailVerified: false,
    phoneVerified: false,
    showEmailArea: false,
    showPhoneArea: false
  })

  const [regInProcess, setRegInProcess] = useState(false)
  const [isUserBoarded, setUserBoarded] = useState(false)

  const handleValChange = (event) => {
    let { value, name } = event.target;
    const phoneRegex = /^[1-9]\d{0,14}$/;
    const passRegex = /^[a-zA-Z0-9@.*$]+$/;

    switch (name) {
      case 'email':
        if (!checkSpace(value) && value?.length > 0) {
          const lowercaseEmail = value?.toLowerCase()
          if (validateEmail(lowercaseEmail)) {
            setEmail(lowercaseEmail);
            setEmailError(false);
          } else {
            setEmail(lowercaseEmail);
            setEmailError(true);
          }
        } else {
          setEmail("");
          setEmailError(true);
        }
        break;

      case 'phone':
        if (value && phoneRegex.test(value) && value?.length <= 15) {
          setPhone(value);
          setPhoneError(false);
        } else {
          setPhone("");
          setPhoneError(true);
        }
        break;

      case 'password':
        if (!checkSpace(value) && value.length > 0) {
          setPassword(value);
          setPasswordError(false);
          if (!passRegex.test(value)) {
            setPassErrMsg('Enter alphabets, numbers and * . @ $ to form a password')
            setTimeout(() => {
              setPassword("");
              setPasswordError(true);
            }, 5000)
          } else if (value?.length < 6) {
            setPassErrMsg('Please enter a password of least 6 characters')
          } else {
            setPassErrMsg("")
          }
        } else {
          setPassErrMsg("")
          setPassword("");
          setPasswordError(true);
        }
        break;

      default:
        break;
    }

  }

  const handleShowOTPAreas = (type) => {
    switch (type) {
      case "email":
        if (email?.trim()?.length > 0 && !emailError) {
          setOtpSection({
            ...otpSection,
            showEmailArea: !otpSection?.showEmailArea
          })
        }
        break;

      case "phone":
        if (phone?.trim()?.length > 0 && !phoneError) {
          setOtpSection({
            ...otpSection,
            showPhoneArea: !otpSection?.showPhoneArea
          })
        }
        break;

      default:
        break;
    }
  }

  const handleIsAgreedChange = () => {
    setIsAgreed(!isAgreed);
    setIsAgreedError(false);
  };

  const validateFields = (name) => {
    switch (name) {
      case "fname":
        if (fname.toString().length > 0) {
          setFnameError(false);
          return false;
        } else {
          setFnameError(true);
          return true;
        }
        break;

      case "lname":
        if (lname.toString().length > 0) {
          setLnameError(false);
          return false;
        } else {
          setLnameError(true);
          return true;
        }
        break;

      case "email":
        if (email.toString().length > 0) {
          if (validateEmail(email)) {
            setEmailError(false);
            return false;
          } else {
            setEmailError(true);
            return true;
          }
        } else {
          setEmailError(true);
          return true;
        }
        break;

      case "password":
        if (password.toString().length > 0) {
          setPasswordError(false);
          return false;
        } else {
          setPasswordError(true);
          return true;
        }
        break;

      case "phone":
        if (phone.toString().length > 0) {
          setPhoneError(false);
          return false;
        } else {
          setPhoneError(true);
          return true;
        }
        break;

      case "country":
        if (country !== null) {
          setCountryError(false);
          return false;
        } else {
          setCountryError(true);
          return true;
        }
        break;

      default:
        break;
    }
  }

  const handleKeyDownSubmit = (event) => {
    if (event?.key === 'Enter') {
      startSignUpProcess();
    }
  };

  const startSignUpProcess = () => {
    const fn = validateFields("fname");
    const ln = validateFields("lname");
    const em = validateFields("email");
    const cnt = validateFields("country");
    const ph = validateFields("phone");
    const pa = validateFields("password");

    if (!fn && !ln && !em && !cnt && !ph && !pa) {
      let finalData = {
        first_name: fname,
        last_name: lname,
        email: email,
        country: country?.id ?? "",
        phone: phone,
        password: password,
        referralLink: referralLink ?? refLink,
        utmLink: utmLink ?? ""
      }

      if (isAgreed) {
        if (!regInProcess) {
          handleUserRegistration(finalData);
        } else {
          dispatch(showToast({ show: true, msg: "Kindly wait, the registration is in process" }));
        }
      } else {
        setIsAgreedError(true);
        dispatch(showToast({ show: true, msg: "Please check the Terms & Conditions to continue..." }));
      }
    } else {
      dispatch(showToast({ show: true, msg: "Please fill all the required fields" }));
    }
  }

  const handleUserRegistration = (userData) => {
    setRegInProcess(true)

    dispatch(
      showLoader({ show: true, msg: "Please wait while we are registering" })
    );
    setTimeout(() => {
      dispatch(
        showLoader({ show: false, msg: "" })
      );
    }, 60 * 1000)

    const response = userRegistrationShortened(userData);
    Promise.resolve(response).then(
      (res) => {
        if (res.code == 200 && !res.error) {

          console.log(response, "thank-you")
          navigate("/thank-you", { state: res })
          // dispatch(getUserSuccess(res));
          setTimeout(() => {
            dispatch(setReferralLink(undefined))
            dispatch(setLinkUTM(undefined))
          }, 3000)
        }
        const displayMessage = res?.errTypeData?.response?.data?.showMessage ?
          res?.errTypeData?.response?.data?.message : res?.message
        dispatch(showToast({ show: true, msg: displayMessage }));
        dispatch(showLoader({ show: false, msg: "" }));
        setRegInProcess(false)
      },
      () => {
        dispatch(showLoader({ show: false, msg: "" }));
        setRegInProcess(false)
      }
    );

  }

  useEffect(() => {
    if (!commonStateCountryData?.length) {
      dispatch(fetchAllList("fetchCountries"));
    } else {
      setCountryData(commonStateCountryData)
    }
  }, [commonStateCountryData])




  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
      }}
    >
      <Helmet>
        <title>
          Register - Acadlance
        </title>

        <meta name="description" content={"Acadlance registration"} />

        <meta
          name="keywords"
          content={"Signup Acadlance, Registration Acadlance"}
        />
      </Helmet>
      <FrontHeader />
      <Box
        className="content-center"
        sx={{
          pt: { xl: "4rem", lg: "4rem", md: "4rem", sm: "1rem", xs: "1rem" },
          pb: { xl: "1rem", lg: "1rem", md: "1rem", sm: "1rem", xs: "1rem" },
          px: { xl: "2rem", lg: "2rem", md: "2rem" },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          flex: 1,
          backgroundColor: '#FFFFFF'
        }}
      >

        <Card sx={{ zIndex: 1 }} onKeyDown={handleKeyDownSubmit}>
          <CardContent sx={{ p: "3rem 2.25rem 1.75rem!important" }}>
            <Box
              onClick={() => navigate("/")}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                columnGap: "1rem",
                mb: 4,
                cursor: 'pointer',
              }}
            >
              <img src="/logo.webp" alt="Logo" />
              <Typography
                variant="span"
                component="span"
                sx={{
                  marginTop: '0.25rem',
                  color: '#6d4cb1',
                  fontSize: '0.75rem',
                  lineHeight: '0.75rem',
                  textAlign: 'center',
                }}
              >
                Pioneering Solution
              </Typography>
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, marginBottom: 0.5 }}
              >
                Adventure starts here
              </Typography>
              <Typography variant="body2">
                Sign up now to unlock exclusive features
              </Typography>
            </Box>
            <Box sx={{
              display: "flex",
              flexDirection: { xl: 'row', lg: 'row', md: 'row', sm: 'column', xs: 'column' },
              columnGap: 3,
              rowGap: 3,
              mb: 3
            }}>
              <CsInput
                autocomplete="off"
                id={"fname"}
                type={"text"}
                label={"First Name"}
                val={fname}
                placeholder={"First Name"}
                error={fnameError === 0 ? false : fnameError}
                errorMsg={nameErrorMsg}
                getValue={(val) => {
                  if (!checkSpace(val) && val.length > 0) {
                    if (validateName(val)) {
                      setFname(val);
                      setFnameError(false);
                    } else {
                      setFname("");
                      setNameErrorMsg("Invalid name");
                      setFnameError(true);
                    }
                  } else {
                    setFname("");
                    setFnameError(true);
                  }
                }}
                autoFocus={true}
                onBlur={() => { }}
              />
              <CsInput
                autocomplete="off"
                id={"lname"}
                type={"text"}
                val={lname}
                label={"Last Name"}
                placeholder={"Last name"}
                error={lnameError === 0 ? false : lnameError}
                errorMsg={nameErrorMsg}
                getValue={(val) => {
                  if (!checkSpace(val) && val.length > 0) {
                    if (validateName(val)) {
                      setLname(val);
                      setLnameError(false);
                    } else {
                      setLname("");
                      setNameErrorMsg("Invalid name");
                      setLnameError(true);
                    }
                  } else {
                    setLname("");
                    setLnameError(true);
                  }
                }}
                onBlur={() => { }}
              />
            </Box>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel htmlFor="email">Email</InputLabel>
              <OutlinedInput
                id="email"
                label="Email"
                value={email}
                name={'email'}
                onChange={handleValChange}
                type={"text"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      // onClick={() => handleShowOTPAreas("email")}
                      aria-label="toggle email visibility"
                      sx={{
                        color: '#6D4CB1',
                        borderRadius: '0.1rem',
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: '#381b72'
                        }
                      }}
                    >
                      <CsTooltip
                        tooltipText={"An OTP will be sent to your email address for account verification"}
                      />
                      {/* <Typography variant="p" sx={{ fontSize: '0.9rem' }}>
                        VERIFY
                      </Typography> */}
                    </IconButton>
                  </InputAdornment>
                }
                error={emailError === 0 ? false : emailError}
              />
              {emailError && emailError !== 0 ? (
                <Error errorMsg={"please fill the email correctly"} />
              ) : null}
            </FormControl>
            {/* {otpSection?.showEmailArea && <Box sx={{ mb: 3 }}>
              <CsInput
                autocomplete="off"
                id={"emailOTP"}
                type={"number"}
                label={isUserBoarded ? "OTP for email" : "Sign up to verify email"}
                val={otpSection?.emailOTP}
                placeholder={isUserBoarded ? "OTP for email verification" : "Sign up to verify email"}
                // error={fnameError === 0 ? false : fnameError}
                // errorMsg={nameErrorMsg}
                getValue={(val) => {
                  if (!checkSpace(val) && val.length < 7) {
                    setOtpSection({
                      ...otpSection,
                      emailOTP: val
                    })
                  } else {
                    setOtpSection({
                      ...otpSection,
                      emailOTP: ""
                    })
                  }
                }}
                disabled={!isUserBoarded}
                cusStyle={{
                  backgroundColor: isUserBoarded ? '#FFFFFF' : '#F4F6F8',
                  borderRadius: '0.25rem'
                }}
                onBlur={() => { }}
              />
            </Box>} */}
            <Box sx={{
              display: "flex",
              flexDirection: { xl: 'row', lg: 'row', md: 'row', sm: 'column', xs: 'column' },
              columnGap: 3,
              rowGap: 3,
              mb: 3
            }}>
              <Box sx={{ width: { xl: "35%", lg: "35%", md: "35%", sm: "100%", xs: '100%' } }}>
                <SearchSelect
                  id={"country"}
                  label={"Country"}
                  placeholder={"Country"}
                  val={country}
                  options={countryData}
                  error={countryError}
                  errorMsg={"please select the country"}
                  getValue={(val) => {
                    if (val.id > 0) {
                      setCountry(val);
                      setCountryError(false);
                    } else {
                      setCountry("");
                      setCountryError(true);
                    }
                  }}
                  onBlur={() => {
                    // onBlur('')
                  }}
                  type="dialCode"
                />
              </Box>
              <Box sx={{ width: { xl: "65%", lg: "65%", md: "65%", sm: "100%", xs: '100%' } }}>
                <FormControl fullWidth >
                  <InputLabel htmlFor="phone">Phone</InputLabel>
                  <OutlinedInput
                    id="phone"
                    label="Phone"
                    value={phone}
                    name="phone"
                    onChange={handleValChange}
                    type={"number"}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          // onClick={() => handleShowOTPAreas("phone")}
                          aria-label="toggle phone visibility"
                          sx={{
                            color: '#6D4CB1',
                            borderRadius: '0.1rem',
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: '#381b72'
                            }
                          }}
                        ><CsTooltip
                            tooltipText={"An OTP will be sent to your phone number for account verification"}
                          />
                          {/* <Typography variant="p" sx={{ fontSize: '0.9rem' }}>
                            VERIFY
                          </Typography> */}
                        </IconButton>
                      </InputAdornment>
                    }
                    error={phoneError === 0 ? false : phoneError}
                  />
                  {phoneError && phoneError !== 0 &&
                    <Error errorMsg={"please fill the phone"} />
                  }
                </FormControl>
              </Box>
            </Box>
            {/* {otpSection?.showPhoneArea && <Box sx={{ mb: 3 }}>
              <CsInput
                autocomplete="off"
                id={"phoneOTP"}
                type={"number"}
                label={isUserBoarded ? "OTP for phone" : "Sign up to verify phone"}
                val={otpSection?.phoneOTP}
                placeholder={isUserBoarded ? "OTP for phone verification" : "Sign up to verify phone"}
                // error={fnameError === 0 ? false : fnameError}
                // errorMsg={nameErrorMsg}
                getValue={(val) => {
                  if (!checkSpace(val) && val.length < 7) {
                    setOtpSection({
                      ...otpSection,
                      phoneOTP: val
                    })
                  } else {
                    setOtpSection({
                      ...otpSection,
                      phoneOTP: ""
                    })
                  }
                }}
                disabled={!isUserBoarded}
                cusStyle={{
                  backgroundColor: isUserBoarded ? '#FFFFFF' : '#F4F6F8',
                  borderRadius: '0.25rem'
                }}
                onBlur={() => { }}
              />
            </Box>} */}
            <FormControl fullWidth>
              <InputLabel htmlFor="auth-login-password">Password</InputLabel>
              <OutlinedInput
                id="auth-login-password"
                label="Password"
                value={password}
                name="password"
                onChange={handleValChange}
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? (
                        <VisibilityIcon />
                      ) : (
                        <VisibilityOffIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                error={passwordError === 0 ? false : passwordError}
              />
              {passwordError && passwordError !== 0 ? (
                <Error errorMsg={"please fill the password"} />
              ) : passErrMsg?.trim()?.length > 0 ? (
                <Error errorMsg={passErrMsg} />
              ) : null}
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  // required
                  checked={isAgreed}
                  onChange={handleIsAgreedChange}
                  sx={{
                    color: isAgreedError && isAgreedError !== 0 ? "red" : "",
                  }}
                />
              }
              label={
                <Box style={{ display: 'flex', alignItems: 'center', columnGap: '0.25rem', flexWrap: 'wrap' }}>
                  <span style={{ minWidth: 'max-content' }}>I agree to </span>
                  <LinkStyled
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ minWidth: 'max-content' }}
                  >
                    privacy policy
                  </LinkStyled> &
                  <LinkStyled
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ minWidth: 'max-content' }}
                  >
                    terms & conditions
                  </LinkStyled>
                  <p>*</p>
                </Box>
              }
            />
            <Button
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              sx={{ marginBottom: 3, marginTop: '-1rem' }}
              onClick={startSignUpProcess}
            >
              Sign up
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '-1rem', marginBottom: '.5rem' }}>
              <hr style={{ flex: 1, border: '0', borderTop: '1px solid #ccc' }} />
              <span style={{ margin: '0 1rem' }}>OR</span>
              <hr style={{ flex: 1, border: '0', borderTop: '1px solid #ccc' }} />
            </Box>
            <Box sx={{ margin:'auto' }}>
              <GoogleSignup btnText={'signup_with'}/>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: "center",marginTop:'.5rem'
              }}
            >
              <Typography variant="body2" sx={{ marginRight: 2 }}>
                Already have an account?
              </Typography>
              <Typography
                variant="body2"
                onClick={() => {
                  navigate("/login");
                }}
              >
                <LinkStyled>Sign in instead</LinkStyled>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Box sx={{ pt: 3, backgroundColor: '#FFFFFF' }}></Box>
      <Footer />
    </Box>
  )

}

export default RegisterPage;
